const BASE_URL = 'https://mongobyte-hc73.onrender.com/api/v1';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const scheduledOrdersApi = {
  // Create scheduled order
  createScheduledOrder: async (orderData) => {
    try {
      // Support both new multiple scheduledTimes format and legacy single scheduledFor
      const formattedData = { ...orderData };
      
      // Format data according to the new API structure if needed
      if (formattedData.scheduledFor && !formattedData.scheduledTimes) {
        // Convert legacy format to new format
        formattedData.scheduledTimes = [{
          scheduledFor: formattedData.scheduledFor,
          meals: formattedData.meals,
          repeatType: formattedData.repeatType || 'none',
          repeatDays: formattedData.repeatDays || [],
          dayTimeSchedule: formattedData.dayTimeSchedule || [],
          endDate: formattedData.endDate
        }];
      }
      
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Create scheduled order error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Preview scheduled order cost
  previewScheduledOrder: async (orderData) => {
    try {
      // Support both new multiple scheduledTimes format and legacy single scheduledFor
      const formattedData = { ...orderData };
      
      // Format data according to the new API structure if needed
      if (formattedData.scheduledFor && !formattedData.scheduledTimes) {
        // Convert legacy format to new format
        formattedData.scheduledTimes = [{
          scheduledFor: formattedData.scheduledFor,
          meals: formattedData.meals,
          repeatType: formattedData.repeatType || 'none',
          repeatDays: formattedData.repeatDays || [],
          dayTimeSchedule: formattedData.dayTimeSchedule || [],
          endDate: formattedData.endDate
        }];
      }
      
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Preview scheduled order error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get restaurants for scheduling
  getRestaurantsForScheduling: async () => {
    try {
      const response = await fetch(`${BASE_URL}/restaurants`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get restaurants error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get meals for scheduling from a restaurant
  getMealsForScheduling: async (restaurantId) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/meal-selection/${restaurantId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // Fallback to regular restaurant endpoint
        const fallbackResponse = await fetch(`${BASE_URL}/restaurants/${restaurantId}`, {
          headers: getAuthHeaders()
        });
        
        if (!fallbackResponse.ok) {
          return { success: false, message: 'Failed to fetch restaurant meals' };
        }
        
        const restaurantData = await fallbackResponse.json();
        if (restaurantData.success) {
          return {
            success: true,
            data: {
              restaurant: restaurantData.data,
              meals: restaurantData.data.meals || [],
              schedulingInfo: {
                schedulingFeePerExecution: 200,
                minimumScheduleTime: "30 minutes from now",
                maxAdvanceScheduling: "30 days"
              }
            }
          };
        }
        
        return { success: false, message: 'Failed to fetch restaurant data' };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get meals for scheduling error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Get user's scheduled orders
  getScheduledOrders: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get scheduled orders error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Cancel scheduled order
  cancelScheduledOrder: async (orderId) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}/cancel`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Cancel scheduled order error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Update scheduled order
  updateScheduledOrder: async (orderId, updates) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Update scheduled order error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },

  // Search users for scheduled order recipients
  searchUsersForScheduledOrder: async (query) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/search-users?query=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Search users error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },
  
  // Search recipients in the same university
  searchRecipientsInUniversity: async (query) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/recipients?query=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Search recipients error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },
  
  // Add a schedule to an existing scheduled order
  addScheduleToOrder: async (orderId, scheduleData) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}/schedule`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(scheduleData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Add schedule error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },
  
  // Get schedule details for a specific scheduled order
  getScheduleDetails: async (orderId, scheduleIndex) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}/schedule/${scheduleIndex}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get schedule details error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },
  
  // Update schedule in a scheduled order
  updateSchedule: async (orderId, scheduleIndex, updates) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}/schedule/${scheduleIndex}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Update schedule error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  },
  
  // Delete a schedule from a scheduled order
  deleteSchedule: async (orderId, scheduleIndex) => {
    try {
      const response = await fetch(`${BASE_URL}/advanced-orders/scheduled/${orderId}/schedule/${scheduleIndex}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, ...errorData };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Delete schedule error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }
};

export const groupOrdersApi = {
  // Create group order
  createGroupOrder: async (groupData) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(groupData)
    });
    return response.json();
  },

  // Get public group orders
  getPublicGroupOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.restaurant) params.append('restaurant', filters.restaurant);
    if (filters.university) params.append('university', 'true');

    const response = await fetch(`${BASE_URL}/advanced-orders/group/public?${params}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Get user's group orders (legacy endpoint)
  getUserGroupOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);

    const response = await fetch(`${BASE_URL}/advanced-orders/group?${params}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Get user's group orders with enhanced filtering (NEW ENDPOINT)
  getMyGroupOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.role) params.append('role', filters.role);

    const response = await fetch(`${BASE_URL}/advanced-orders/group/my?${params}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Join group order
  joinGroupOrder: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/join`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Add meals to group order
  addMealsToGroupOrder: async (orderId, meals) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/meals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ meals })
    });
    return response.json();
  },

  // Send chat message
  sendChatMessage: async (orderId, message) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/chat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message })
    });
    return response.json();
  },

  // Get chat messages
  getChatMessages: async (orderId, page = 1, limit = 50) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/chat?page=${page}&limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Pay for group order
  payForGroupOrder: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/pay`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Get payment status
  getPaymentStatus: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/payment-status`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Get group order details
  getGroupOrderDetails: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Leave group order
  leaveGroupOrder: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/leave`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Cancel group order (creator only)
  cancelGroupOrder: async (orderId) => {
    const response = await fetch(`${BASE_URL}/advanced-orders/group/${orderId}/cancel`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Get restaurant meals for group order
  getRestaurantMeals: async (restaurantId) => {
    const response = await fetch(`${BASE_URL}/restaurants/${restaurantId}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  }
};